import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import {
	AlertTriangle,
	MinusCircle,
	Package,
	PlusCircle,
	ShoppingCart,
	Trash,
	Truck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface CartModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

interface SupplierInfo {
	id: string;
	name: string | null;
	business_name: string | null;
}

interface SupplierProfile {
	id: string;
	name: string | null;
	business_name: string | null;
	account_type: string;
	minimum_free_shipping: number | null;
	shipping_cost: number;
	minimum_order: number;
	shipping_cost_type: string;
}

export function CartModal({ open, onOpenChange }: CartModalProps) {
	const {
		items,
		removeFromCart,
		updateQuantity,
		totalItems,
		totalUnits,
		supplierShippingDetails,
	} = useCart();
	const navigate = useNavigate();
	const [supplierInfo, setSupplierInfo] = useState<
		Record<string, SupplierInfo>
	>({});
	const [supplierProfiles, setSupplierProfiles] = useState<
		Record<string, SupplierProfile>
	>({});

	useEffect(() => {
		const fetchSupplierInfo = async () => {
			const supplierIds = [
				...new Set(items.map((item) => item.supplierId)),
			];
			if (supplierIds.length === 0) return;

			try {
				const { data, error } = await supabase
					.from("profiles")
					.select("id, name, business_name")
					.in("id", supplierIds);

				if (error) throw error;

				const supplierMap: Record<string, SupplierInfo> = {};
				data.forEach((supplier) => {
					supplierMap[supplier.id] = supplier;
				});

				setSupplierInfo(supplierMap);
			} catch (error) {
				console.error("Error fetching supplier info:", error);
			}
		};

		const fetchSupplierProfiles = async () => {
			const supplierIds = [
				...new Set(items.map((item) => item.supplierId)),
			];
			if (supplierIds.length === 0) return;

			try {
				const { data, error } = await supabase
					.from("profiles")
					.select(
						"id, name, business_name, minimum_free_shipping, shipping_cost, minimum_order, account_type, shipping_cost_type"
					)
					.in("id", supplierIds);

				if (error) throw error;

				const profileMap: Record<string, SupplierProfile> = {};
				data.forEach((profile) => {
					profileMap[profile.id] = profile;
				});

				setSupplierProfiles(profileMap);
			} catch (error) {
				console.error("Error fetching supplier profiles:", error);
			}
		};

		fetchSupplierInfo();
		fetchSupplierProfiles();
	}, [items]);

	const handleNavigateToCheckout = () => {
		onOpenChange(false);
		navigate("/checkout");
	};

	const itemsBySupplier: Record<string, typeof items> = {};
	items.forEach((item) => {
		if (!itemsBySupplier[item.supplierId]) {
			itemsBySupplier[item.supplierId] = [];
		}
		itemsBySupplier[item.supplierId].push(item);
	});

	const isShippingFree = (
		supplierId: string,
		subtotal: number,
		allSamples: boolean
	) => {
		const profile = supplierProfiles[supplierId];
		if (!profile) return false;
		if (allSamples) return true;
		if (profile.minimum_free_shipping === null) return false;
		if (profile.minimum_free_shipping === 0) return false;
		return subtotal >= profile.minimum_free_shipping;
	};

	const calculateShippingCost = (
		supplierId: string,
		subtotal: number,
		allSamples: boolean
	) => {
		if (allSamples) return 0;
		if (isShippingFree(supplierId, subtotal, allSamples)) return 0;

		const profile = supplierProfiles[supplierId];
		if (!profile) return 0;

		if (profile.shipping_cost_type === "percentage") {
			return (subtotal * profile.shipping_cost) / 100;
		} else {
			return profile.shipping_cost || 0;
		}
	};

	if (items.length === 0) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent className="w-full sm:max-w-md overflow-y-auto">
					<SheetHeader className="mb-6">
						<SheetTitle>Your Cart</SheetTitle>
						<SheetDescription>
							Your shopping cart is empty
						</SheetDescription>
					</SheetHeader>
					<div className="flex flex-col items-center justify-center h-[50vh] text-center gap-4">
						<ShoppingCart className="h-16 w-16 text-gray-300" />
						<h3 className="text-lg font-medium">
							Your cart is empty
						</h3>
						<p className="text-sm text-gray-500 max-w-xs">
							Looks like you haven't added any products to your
							cart yet.
						</p>
						<Button
							className="mt-4"
							onClick={() => onOpenChange(false)}>
							Continue Shopping
						</Button>
					</div>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="w-full sm:max-w-md overflow-y-auto">
				<SheetHeader className="mb-6">
					<SheetTitle>Your Cart ({totalItems} items)</SheetTitle>
					<SheetDescription>
						{totalUnits} total units in cart
					</SheetDescription>
				</SheetHeader>

				<div className="space-y-6">
					{Object.entries(itemsBySupplier).map(
						([supplierId, supplierItems]) => {
							const supplierDetails = supplierInfo[supplierId];
							const supplierShipping =
								supplierShippingDetails.find(
									(s) => s.supplierId === supplierId
								);

							// Add this check for all samples
							const allSamplesForSupplier = supplierItems.every(
								(item) => item.isSample
							);

							// Calculate totals for this supplier
							const supplierSubtotal = supplierItems.reduce(
								(total, item) =>
									total +
									item.price *
										item.quantity *
										item.caseQuantity,
								0
							);
							const supplierShippingCost = calculateShippingCost(
								supplierId,
								supplierSubtotal,
								allSamplesForSupplier
							);
							const supplierTotal =
								supplierSubtotal + supplierShippingCost;
							const minimumOrder =
								supplierShipping?.minimumOrder || 0;
							const meetsMinimumOrder =
								allSamplesForSupplier ||
								minimumOrder === 0 ||
								supplierSubtotal >= minimumOrder;

							return (
								<div key={supplierId} className="space-y-4">
									<div className="bg-gray-50 p-3 rounded-md">
										<div className="flex justify-between items-center">
											<span className="font-medium text-sm">
												{supplierDetails?.business_name ||
													supplierDetails?.name ||
													`Supplier ${supplierId}`}
											</span>
											<div className="text-xs flex items-center">
												<Truck className="h-3 w-3 mr-1" />
												{isShippingFree(
													supplierId,
													supplierSubtotal,
													allSamplesForSupplier
												) ? (
													<span className="text-green-600">
														Free Shipping
													</span>
												) : (
													<>
														<span className="text-gray-500">
															{formatCurrency(
																supplierShippingCost
															)}{" "}
															shipping
															{supplierProfiles[
																supplierId
															]
																?.shipping_cost_type ===
																"percentage" && (
																<span className="ml-1 text-gray-400">
																	(
																	{
																		supplierProfiles[
																			supplierId
																		]
																			.shipping_cost
																	}
																	%)
																</span>
															)}
														</span>
														{supplierProfiles[
															supplierId
														]
															?.minimum_free_shipping >
															0 && (
															<span className="ml-1 text-gray-400">
																(Free over{" "}
																{formatCurrency(
																	supplierProfiles[
																		supplierId
																	]
																		.minimum_free_shipping
																)}
																)
															</span>
														)}
													</>
												)}
											</div>
										</div>

										{minimumOrder > 0 &&
											!allSamplesForSupplier && (
												<div
													className={`text-xs flex items-center mt-2 ${
														!meetsMinimumOrder
															? "text-amber-600"
															: "text-green-600"
													}`}>
													{!meetsMinimumOrder ? (
														<>
															<AlertTriangle className="h-3 w-3 mr-1" />
															<span>
																Minimum order:{" "}
																{formatCurrency(
																	minimumOrder
																)}
																(Current:{" "}
																{formatCurrency(
																	supplierSubtotal
																)}
																)
															</span>
														</>
													) : (
														<span>
															✓ Minimum order of{" "}
															{formatCurrency(
																minimumOrder
															)}{" "}
															met
														</span>
													)}
												</div>
											)}
									</div>

									{supplierItems.map((item) => (
										<div
											key={item.id}
											className="flex gap-4">
											<div className="h-20 w-20 rounded-md overflow-hidden bg-gray-50 flex-shrink-0">
												<img
													src={item.image}
													alt={item.name}
													className="h-full w-full object-cover"
												/>
											</div>
											<div className="flex-1 min-w-0">
												<h4 className="text-sm font-medium text-gray-900 truncate">
													{item.name}
												</h4>
												<p className="mt-1 text-sm text-gray-500">
													{formatCurrency(item.price)}{" "}
													×{" "}
													{item.quantity *
														item.caseQuantity}{" "}
													units
												</p>
												<p className="text-xs text-gray-500">
													<Package className="inline-block h-3 w-3 mr-1" />
													{item.quantity} cases (
													{item.caseQuantity} units
													per case)
												</p>
												<div className="mt-2 flex items-center gap-3">
													<Button
														variant="outline"
														size="icon"
														className="h-7 w-7"
														onClick={() =>
															updateQuantity(
																item.id,
																item.quantity -
																	1
															)
														}
														disabled={
															item.quantity <= 1
														}>
														<MinusCircle className="h-4 w-4" />
													</Button>
													<span className="text-sm font-medium">
														{item.quantity}
													</span>
													<Button
														variant="outline"
														size="icon"
														className="h-7 w-7"
														onClick={() =>
															updateQuantity(
																item.id,
																item.quantity +
																	1
															)
														}>
														<PlusCircle className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 ml-auto text-gray-400"
														onClick={() =>
															removeFromCart(
																item.id
															)
														}>
														<Trash className="h-4 w-4" />
													</Button>
												</div>
											</div>
										</div>
									))}

									<div className="mt-4 space-y-3 border-t pt-3">
										<div className="flex justify-between text-sm">
											<span className="text-gray-500">
												Subtotal
											</span>
											<span>
												{formatCurrency(
													supplierSubtotal
												)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-500">
												Shipping
											</span>
											{supplierShippingCost > 0 ? (
												<span>
													{formatCurrency(
														supplierShippingCost
													)}
												</span>
											) : (
												<span className="text-green-600">
													Free
												</span>
											)}
										</div>
										<Separator />
										<div className="flex justify-between font-medium">
											<span>Total</span>
											<span>
												{formatCurrency(supplierTotal)}
											</span>
										</div>
										<Button
											className="w-full mt-2"
											onClick={() => {
												onOpenChange(false);
												navigate(
													`/checkout/${supplierId}`
												);
											}}
											disabled={
												!meetsMinimumOrder &&
												minimumOrder > 0
											}>
											{!meetsMinimumOrder &&
											minimumOrder > 0
												? `Add ${formatCurrency(
														minimumOrder -
															supplierSubtotal
												  )} more to checkout`
												: `Checkout with ${
														supplierDetails?.business_name ||
														supplierDetails?.name ||
														"Supplier"
												  }`}
										</Button>
									</div>
								</div>
							);
						}
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
